import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  User, Users, Shield, AlertTriangle, FileSearch, Server, UserCheck,
  ChevronRight, Sparkles, Building2, ShoppingBag, ShieldCheck, Activity, Brain, Plus, Wand2, SlidersHorizontal, X
} from "lucide-react";
import API from "../lib/api";

const AGENT_ICONS = {
  UserCheck: UserCheck,
  AlertTriangle: AlertTriangle,
  FileSearch: FileSearch,
  Shield: Shield,
  Server: Server,
  User: User,
  Activity: Activity
};

export function AuditModeSelector({ onSelect, onCancel }) {
  const [mode, setMode] = useState("solo");
  const [templates, setTemplates] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("it_governance");
  const [teamSetupMode, setTeamSetupMode] = useState("template");
  const [customTeamConfig, setCustomTeamConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [{ data: templateData }, { data: personaData }] = await Promise.all([
          API.get("/team-templates"),
          API.get("/agent-personas")
        ]);
        setTemplates(templateData.templates || []);
        setPersonas(personaData.agents || []);
      } catch (err) {
        console.error("Failed to fetch team config:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!templates.length || !personas.length) return;
    setCustomTeamConfig((current) => current || buildCustomTeamConfig(selectedTemplate, templates, personas));
  }, [templates, personas, selectedTemplate]);

  const handleConfirm = () => {
    onSelect({
      audit_mode: mode,
      team_template: mode === "team" && teamSetupMode === "template" ? selectedTemplate : null,
      custom_team_config: mode === "team" && teamSetupMode === "custom" ? customTeamConfig : null
    });
  };

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);
  const availablePersonaOptions = personas.filter((persona) => !customTeamConfig?.agents?.some((agent) => agent.agent_id === persona.agent_id));

  const handleResetCustomTeam = () => {
    setCustomTeamConfig(buildCustomTeamConfig(selectedTemplate, templates, personas));
  };

  const updateCustomAgent = (agentId, field, value) => {
    setCustomTeamConfig((current) => ({
      ...(current || {}),
      agents: (current?.agents || []).map((agent) =>
        agent.agent_id === agentId ? { ...agent, [field]: value } : agent
      ),
    }));
  };

  const removeCustomAgent = (agentId) => {
    setCustomTeamConfig((current) => ({
      ...(current || {}),
      agents: (current?.agents || []).filter((agent) => agent.agent_id !== agentId),
    }));
  };

  const addCustomAgent = (agentId) => {
    const persona = personas.find((entry) => entry.agent_id === agentId);
    if (!persona) return;
    setCustomTeamConfig((current) => ({
      ...(current || {}),
      agents: [...(current?.agents || []), buildCustomAgent(persona)],
    }));
  };

  const updateRiskAppetite = (value) => {
    setCustomTeamConfig((current) => ({
      ...(current || {}),
      risk_appetite: value,
    }));
  };

  return (
    <div className="flex flex-col max-h-[70vh]">
      <div className="flex-1 overflow-y-auto pr-2 space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">Select Audit Mode</h3>
          <p className="text-sm text-muted-foreground">
            Choose how you want to conduct this audit
          </p>
        </div>

        <RadioGroup value={mode} onValueChange={setMode} className="grid gap-4">
          {/* Solo Mode */}
          <Label
            htmlFor="solo"
            className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${mode === "solo" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              }`}
          >
            <RadioGroupItem value="solo" id="solo" className="mt-1" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span className="font-medium">Solo Auditor Mode</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Single AI auditor assistant guides you through the audit process.
                Best for straightforward audits or when you want full control.
              </p>
            </div>
          </Label>

          {/* Team Mode */}
          <Label
            htmlFor="team"
            className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${mode === "team" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              }`}
          >
            <RadioGroupItem value="team" id="team" className="mt-1" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span className="font-medium">Audit Team Mode</span>
                <Badge variant="secondary" className="text-xs">
                  <Sparkles className="w-3 h-3 mr-1" /> Multi-Agent
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Multiple AI auditor personas collaborate, challenge, and validate findings.
                Like working with a mini audit department inside Zentis.
              </p>
            </div>
          </Label>
        </RadioGroup>

        {/* Team Template Selection */}
        {mode === "team" && !loading && (
          <div className="space-y-4 animate-in slide-in-from-top-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTeamSetupMode("template")}
                className={`rounded-lg border px-3 py-2 text-left transition-all ${teamSetupMode === "template" ? "border-primary bg-primary/8 text-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Wand2 className="h-4 w-4" /> Preset Team
                </div>
                <p className="mt-1 text-xs">Use the existing template roster.</p>
              </button>
              <button
                type="button"
                onClick={() => {
                  setTeamSetupMode("custom");
                  if (!customTeamConfig) {
                    setCustomTeamConfig(buildCustomTeamConfig(selectedTemplate, templates, personas));
                  }
                }}
                className={`rounded-lg border px-3 py-2 text-left transition-all ${teamSetupMode === "custom" ? "border-primary bg-primary/8 text-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  <SlidersHorizontal className="h-4 w-4" /> Custom Team
                </div>
                <p className="mt-1 text-xs">Tune persona, behavior, and prompts.</p>
              </button>
            </div>

            {teamSetupMode === "template" ? (
              <>
            <div>
              <h4 className="text-sm font-medium mb-2">Select Team Template</h4>
              <p className="text-xs text-muted-foreground">
                Choose a pre-configured team or customize your own
              </p>
            </div>

            <div className="grid gap-2">
              {/* Banking / Financial Templates */}
              <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mt-1">
                <Building2 className="w-3 h-3" /> Banking &amp; Financial
              </p>
              {templates.filter(t => !["platform_integrity", "partner_ecosystem", "technology_governance"].includes(t.id)).map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedTemplate === template.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{template.name}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{template.agents?.length || 0} agents</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.recommended_for?.map((rec, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{rec}</Badge>
                    ))}
                  </div>
                </div>
              ))}

              {/* Platform / E-Commerce Templates */}
              {templates.some(t => ["platform_integrity", "partner_ecosystem", "technology_governance"].includes(t.id)) && (
                <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mt-2">
                  <ShoppingBag className="w-3 h-3" /> Platform &amp; E-Commerce
                </p>
              )}
              {templates.filter(t => ["platform_integrity", "partner_ecosystem", "technology_governance"].includes(t.id)).map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedTemplate === template.id
                      ? "border-amber-500 bg-amber-500/5"
                      : "border-border hover:border-amber-400/50"
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{template.name}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
                    </div>
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 dark:text-amber-400">{template.agents?.length || 0} agents</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.recommended_for?.map((rec, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{rec}</Badge>
                    ))}
                  </div>
                </div>
              ))}

              {/* Insurance Templates */}
              {templates.some(t => t.id.startsWith("insurance_")) && (
                <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mt-2">
                  <ShieldCheck className="w-3 h-3" /> Insurance
                </p>
              )}
              {templates.filter(t => t.id.startsWith("insurance_")).map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedTemplate === template.id
                      ? "border-blue-500 bg-blue-500/5"
                      : "border-border hover:border-blue-400/50"
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{template.name}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
                    </div>
                    <Badge variant="outline" className="text-xs border-blue-400 text-blue-600 dark:text-blue-400">{template.agents?.length || 0} agents</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.recommended_for?.map((rec, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{rec}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Team Preview */}
            {selectedTemplateData && (
              <Card className="bg-muted/30">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Team Roster Preview</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="grid gap-2">
                    {selectedTemplateData.agents?.map((agentId, i) => {
                      const agentNames = {
                        lead_auditor: { name: "Lead Auditor", icon: "UserCheck", desc: "Orchestrator" },
                        skeptical_watchdog: { name: "Skeptical Watchdog", icon: "AlertTriangle", desc: "Adversarial" },
                        detail_perfectionist: { name: "Detail Perfectionist", icon: "FileSearch", desc: "Controls" },
                        compliance_guardian: { name: "Compliance Guardian", icon: "Shield", desc: "Regulatory" },
                        it_specialist: { name: "IT Specialist", icon: "Server", desc: "ITGC/Cyber" },
                        underwriting_specialist: { name: "Underwriting Specialist", icon: "Activity", desc: "Medical & Financial" }
                      };
                      const agent = agentNames[agentId] || { name: agentId, icon: "User", desc: "" };
                      const IconComponent = AGENT_ICONS[agent.icon] || User;

                      return (
                        <div key={i} className="flex items-center gap-3 p-2 rounded bg-background/50">
                          <IconComponent className="w-4 h-4 text-muted-foreground" />
                          <div className="flex-1">
                            <span className="text-sm font-medium">{agent.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">{agent.desc}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <div>
                    <h4 className="text-sm font-medium">Custom Audit Team Builder</h4>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Start from the selected template, then tune each agent’s persona, specialty focus, behavior notes, and system prompt.
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={handleResetCustomTeam}>
                    Start From Template
                  </Button>
                </div>

                <div className="grid gap-3 md:grid-cols-[1.2fr_0.8fr]">
                  <div className="space-y-3">
                    <div>
                      <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Team Name
                      </Label>
                      <Input
                        value={customTeamConfig?.template_name || ""}
                        onChange={(e) => setCustomTeamConfig((current) => ({ ...(current || {}), template_name: e.target.value }))}
                        placeholder="Custom banking team"
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Risk Appetite
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {["conservative", "balanced", "aggressive"].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => updateRiskAppetite(option)}
                            className={`rounded-full border px-3 py-1 text-xs transition-all ${customTeamConfig?.risk_appetite === option ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <Card className="bg-muted/20">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Add Specialist</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 py-2">
                      {availablePersonaOptions.length === 0 ? (
                        <p className="text-xs text-muted-foreground">All available personas are already in your team.</p>
                      ) : availablePersonaOptions.map((persona) => (
                        <button
                          key={persona.agent_id}
                          type="button"
                          onClick={() => addCustomAgent(persona.agent_id)}
                          className="flex w-full items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2 text-left transition-all hover:border-primary/40 hover:bg-primary/5"
                        >
                          <div>
                            <p className="text-sm font-medium">{persona.name}</p>
                            <p className="text-xs text-muted-foreground">{persona.role.replaceAll("_", " ")}</p>
                          </div>
                          <Plus className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-3">
                  {(customTeamConfig?.agents || []).map((agent) => {
                    const IconComponent = AGENT_ICONS[agent.icon] || Brain;
                    return (
                      <Card key={agent.agent_id} className="bg-muted/20">
                        <CardHeader className="py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="rounded-lg border border-primary/20 bg-primary/10 p-2">
                                <IconComponent className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <CardTitle className="text-sm">{agent.name}</CardTitle>
                                <p className="text-xs text-muted-foreground">{agent.role.replaceAll("_", " ")}</p>
                              </div>
                            </div>
                            {agent.agent_id !== "lead_auditor" && (
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeCustomAgent(agent.agent_id)}>
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Agent Name</Label>
                              <Input value={agent.name} onChange={(e) => updateCustomAgent(agent.agent_id, "name", e.target.value)} />
                            </div>
                            <div>
                              <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Specialty Areas</Label>
                              <Input
                                value={(agent.specialty_areas || []).join(", ")}
                                onChange={(e) => updateCustomAgent(agent.agent_id, "specialty_areas", e.target.value.split(",").map((item) => item.trim()).filter(Boolean))}
                                placeholder="risk, controls, compliance"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Persona Summary</Label>
                            <Textarea
                              value={agent.persona_summary || ""}
                              onChange={(e) => updateCustomAgent(agent.agent_id, "persona_summary", e.target.value)}
                              placeholder="How this agent should think and sound"
                              className="min-h-[72px]"
                            />
                          </div>
                          <div>
                            <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Behavior Instructions</Label>
                            <Textarea
                              value={agent.behavior_instructions || ""}
                              onChange={(e) => updateCustomAgent(agent.agent_id, "behavior_instructions", e.target.value)}
                              placeholder="Challenge assumptions, be conservative on weak evidence, escalate policy gaps..."
                              className="min-h-[72px]"
                            />
                          </div>
                          <div>
                            <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">System Prompt</Label>
                            <Textarea
                              value={agent.system_prompt || ""}
                              onChange={(e) => updateCustomAgent(agent.agent_id, "system_prompt", e.target.value)}
                              className="min-h-[180px] font-mono text-[11px]"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fixed footer with buttons - always visible */}
      <div className="flex justify-end gap-2 pt-4 border-t mt-4 shrink-0">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={mode === "team" && teamSetupMode === "custom" && !(customTeamConfig?.agents || []).length}>
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function buildCustomAgent(persona) {
  return {
    agent_id: persona.agent_id,
    name: persona.name,
    role: persona.role,
    icon: persona.icon,
    description: persona.description || "",
    persona_summary: persona.description || "",
    behavior_instructions: "",
    specialty_areas: Array.isArray(persona.specialty_areas) ? persona.specialty_areas : [],
    system_prompt: persona.system_prompt || "",
    status: "idle"
  };
}

function buildCustomTeamConfig(templateId, templates, personas) {
  const template = templates.find((entry) => entry.id === templateId) || templates[0];
  const personaMap = new Map(personas.map((persona) => [persona.agent_id, persona]));
  return {
    template_id: "custom",
    template_name: template ? `Custom ${template.name}` : "Custom Audit Team",
    risk_appetite: template?.risk_appetite || "balanced",
    agents: (template?.agents || [])
      .map((agentId) => personaMap.get(agentId))
      .filter(Boolean)
      .map(buildCustomAgent)
  };
}

export default AuditModeSelector;
